import { QueryClientProvider } from '@tanstack/react-query';
import { initializeMsal } from './auth/msalConfig';
import { AuthProvider } from './auth/AuthContext';
import { FirmConfigProvider } from './contexts/FirmConfigContext';
import App from './App';
import DemoPasswordGate from './components/DemoPasswordGate';
import { applyThemePreference } from './utils/theme';
import { registerInstallPromptListener } from './hooks/useInstallPrompt';
import { registerServiceWorkerNavigationBridge } from './utils/swNavigationBridge';
import { bootstrapApp } from './bootstrapApp';
import { createAppQueryClient } from './queryClient';
import './index.css';

applyThemePreference();

const queryClient = createAppQueryClient();

// This entry module must both start the app and provide the bootstrap wrapper.
// eslint-disable-next-line react-refresh/only-export-components
function ValentineApp() {
  return (
    <DemoPasswordGate>
      <QueryClientProvider client={queryClient}>
        <AuthProvider msalInstance={msalInstance!} config={msalConfig!}>
          <FirmConfigProvider>
            <App />
          </FirmConfigProvider>
        </AuthProvider>
      </QueryClientProvider>
    </DemoPasswordGate>
  );
}

let msalInstance: Awaited<ReturnType<typeof initializeMsal>>['instance'] | undefined;
let msalConfig: Awaited<ReturnType<typeof initializeMsal>>['config'] | undefined;

async function bootstrap() {
  // P3-A8: register the beforeinstallprompt/appinstalled stash FIRST, before
  // MSAL or React render anything — the browser only ever fires
  // beforeinstallprompt once, and only if something has already called
  // preventDefault() + stashed it; a prompt firing during the loading screen
  // must never be missed.
  registerInstallPromptListener();
  // P3-A12/PP12: wire the receiving half of the SW's notificationclick
  // hand-off (src/sw.ts) — re-dispatches a {type:'NAVIGATE', url} postMessage
  // as a window CustomEvent that useSwNavigationBridge (mounted inside
  // <BrowserRouter> in App.tsx) routes via react-router. Registering this
  // early (navigator.serviceWorker itself doesn't need a registration to
  // exist yet for addEventListener to be valid) means no message can arrive
  // before something is listening.
  registerServiceWorkerNavigationBridge();

  const { instance, config } = await initializeMsal();
  msalInstance = instance;
  msalConfig = config;
  // Register only after MSAL has processed an auth-redirect URL. Reloading
  // mid code-exchange would consume the OAuth code and break the login loop.
  await bootstrapApp({ surface: 'valentine', AppComponent: ValentineApp });
}

bootstrap().catch((err) => {
  console.error('Bootstrap failed:', err);
  // Render a minimal error message so the screen isn't blank
  document.getElementById('root')!.innerHTML =
    '<div style="padding:2rem;font-family:sans-serif;color:#ef4444"><h2>Failed to start</h2><pre>' +
    String(err) + '</pre></div>';
});
